/**
 * The browser half of a passkey ceremony.
 *
 * WebAuthn speaks ArrayBuffers; JSON does not. Everything here is the
 * translation between the two, plus honest feature detection. There is no
 * cryptography in this file and there must never be any: the signing happens
 * inside the device's secure hardware, and the verifying happens on the server
 * with a maintained library.
 */

/** Standard base64url — no padding, `-` and `_` instead of `+` and `/`. */
export function base64UrlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Is WebAuthn available at all? False in old browsers and over plain HTTP. */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.create === 'function' &&
    typeof navigator.credentials?.get === 'function'
  );
}

/**
 * Does this device have a built-in authenticator — Face ID, Touch ID, Windows
 * Hello — as opposed to only a USB key?
 *
 * Asked before the button is offered so a device that cannot do it is not
 * promised something it will refuse.
 */
export async function hasPlatformAuthenticator(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * What to call the button on this device.
 *
 * Deliberately conservative. «Face ID» is claimed only where the platform
 * genuinely means Face ID, and the honest generic — «Войти с Passkey» — is the
 * default everywhere else, because the system dialog is about to name the real
 * mechanism anyway. Promising Face ID to somebody whose device will ask for a
 * PIN is worse than saying nothing: it makes the correct prompt look wrong.
 *
 * Note this reads the User-Agent for wording only. Nothing here is used for
 * identification, sent to the server, or stored.
 */
export function passkeyMethodLabel(): string {
  if (typeof navigator === 'undefined') return 'Passkey';
  const ua = navigator.userAgent;
  const isApple = /Mac|iPhone|iPad|iPod/.test(ua);
  // Touch ID and Face ID are both Apple's, and which one a given Mac or
  // iPhone uses is not something the web can ask. Naming both is accurate
  // for the whole platform and matches what the system dialog will show.
  if (isApple) return 'Face ID / Touch ID';
  return 'Passkey';
}

/** Registration options as the API returns them, ready for the browser. */
type CreationOptionsJSON = {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
  authenticatorSelection?: Record<string, unknown>;
  attestation?: string;
};

type RequestOptionsJSON = {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
  userVerification?: string;
};

function descriptors(
  list: Array<{ id: string; type: 'public-key'; transports?: string[] }> | undefined,
): PublicKeyCredentialDescriptor[] | undefined {
  if (!list?.length) return undefined;
  return list.map((item) => ({
    id: base64UrlToBuffer(item.id),
    type: 'public-key' as const,
    transports: item.transports as AuthenticatorTransport[] | undefined,
  }));
}

/** Raised when the person cancelled, rather than when something went wrong. */
export class PasskeyCancelled extends Error {
  constructor() {
    super('cancelled');
    this.name = 'PasskeyCancelled';
  }
}

function rethrow(error: unknown): never {
  // NotAllowedError covers both «the user dismissed the dialog» and «it timed
  // out». Neither is a failure worth showing as an error banner.
  if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError')) {
    throw new PasskeyCancelled();
  }
  throw error;
}

/** Run `navigator.credentials.create()` and serialise the answer. */
export async function createCredential(options: CreationOptionsJSON): Promise<unknown> {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.create({
      publicKey: {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        user: {
          ...options.user,
          id: base64UrlToBuffer(options.user.id),
        },
        excludeCredentials: descriptors(options.excludeCredentials),
      } as PublicKeyCredentialCreationOptions,
    });
  } catch (error) {
    rethrow(error);
  }
  if (!credential) throw new PasskeyCancelled();

  const result = credential as PublicKeyCredential;
  const response = result.response as AuthenticatorAttestationResponse;
  return {
    id: result.id,
    rawId: bufferToBase64Url(result.rawId),
    type: result.type,
    authenticatorAttachment: result.authenticatorAttachment ?? undefined,
    clientExtensionResults: result.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      attestationObject: bufferToBase64Url(response.attestationObject),
      // Recorded so the next ceremony can offer the right prompt. Optional in
      // some browsers, so guarded rather than assumed.
      transports:
        typeof response.getTransports === 'function' ? response.getTransports() : undefined,
    },
  };
}

/** Run `navigator.credentials.get()` and serialise the answer. */
export async function getAssertion(options: RequestOptionsJSON): Promise<unknown> {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.get({
      publicKey: {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        allowCredentials: descriptors(options.allowCredentials),
      } as PublicKeyCredentialRequestOptions,
    });
  } catch (error) {
    rethrow(error);
  }
  if (!credential) throw new PasskeyCancelled();

  const result = credential as PublicKeyCredential;
  const response = result.response as AuthenticatorAssertionResponse;
  return {
    id: result.id,
    rawId: bufferToBase64Url(result.rawId),
    type: result.type,
    authenticatorAttachment: result.authenticatorAttachment ?? undefined,
    clientExtensionResults: result.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      signature: bufferToBase64Url(response.signature),
      // The discoverable half: this is what lets the server work out who is
      // signing in without ever having been told an email address.
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : undefined,
    },
  };
}
